import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

export enum StatusCode {
  SUCCESS = 200,
  CREATED = 201,
  NO_CONTENT = 204,
  BAD_REQUEST = 400,
  NOT_FOUND = 404,
  INTERNAL_SERVER_ERROR = 500,
}

export class ApiManager {
  private static instance: ApiManager;

  private axiosInstance: AxiosInstance;

  private accessToken?: string;

  private refreshToken?: string;

  private constructor(accessToken?: string) {
    const APIBaseURL = import.meta.env.VITE_API_SERVER_URL;

    this.accessToken = accessToken;
    this.axiosInstance = axios.create({
      baseURL: `${APIBaseURL}`,
      // Cloud run cold start can sometimes reach 10 seconds
      timeout: 20000,
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
  }

  static getInstance = (accessToken?: string): AxiosInstance => {
    if (!ApiManager.instance) {
      ApiManager.instance = new ApiManager(accessToken);
    }

    return ApiManager.instance.axiosInstance;
  };

  static setTokens = (params: { accessToken?: string; refreshToken?: string }) => {
    if (!ApiManager.instance) {
      ApiManager.instance = new ApiManager(params.accessToken);
    }

    const manager = ApiManager.instance;

    manager.accessToken = params.accessToken;
    manager.refreshToken = params.refreshToken;

    // Update default header for subsequent requests
    const defaults = manager.axiosInstance.defaults;
    if (params.accessToken) {
      if (!defaults.headers) {
        (defaults as any).headers = {} as AxiosRequestConfig['headers'];
      }
      // Axios v1 stores common headers under headers.common
      (defaults.headers as any).common = {
        ...((defaults.headers as any).common || {}),
        Authorization: `Bearer ${params.accessToken}`,
      };
    } else {
      if (defaults.headers && (defaults.headers as any).common) {
        delete (defaults.headers as any).common.Authorization;
      }
    }
  };

  static getTokens = () => {
    if (!ApiManager.instance) {
      return {
        accessToken: undefined,
        refreshToken: undefined,
      };
    }

    const { instance } = ApiManager;
    return {
      accessToken: instance.accessToken,
      refreshToken: instance.refreshToken,
    };
  };

  static getAccessToken = () => {
    if (!ApiManager.instance) {
      return undefined;
    }

    return ApiManager.instance.accessToken;
  };
}
